import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useSelector, useStore } from 'react-redux';
import { portalApi, fetchBackgroundData, fullScheduleRequest } from '../redux/api/portalApi';
import { formActivity } from './formActivity';
import { useRealtimeResource, useRealtimeState } from './RealtimeProvider';

const statuses = {
  shipped: ['Shipped'], canceled: ['canceled'], ready: ['Ready To Schedule'], hold: ['*Hold*'],
  release: ['Release with Qualified Exceptions', 'Release Pending CSO', 'Release with CSO Pending',
    'Release w/ CSO Pending', 'Engineering Only', 'Long Lead Only'],
};
export function scheduleUrls(base, view) {
  return statuses[view] ? statuses[view].map(status => `${base}${base.includes('?') ? '&' : '?'}jobStatus=${encodeURIComponent(status)}`) : [base];
}
export function readScheduleRows(payload) {
  const core = payload?.data ?? payload;
  if (!core || typeof core !== 'object') throw new Error('INVALID_SCHEDULE_SNAPSHOT');
  const entries = Object.values(core);
  const rows = entries.map(item => {
    const project = item?.GeneralProjectInfo ?? item?.generalProjectInfo;
    return project ? { ...project, SoldToShipTo: item.SoldToShipTo ?? item.soldToShipTo ?? [] } : (Array.isArray(core) ? item : null);
  });
  if (rows.some(row => !row || Array.isArray(row) || typeof row !== 'object' || (row.ID ?? row.id) == null)) {
    throw new Error('INVALID_SCHEDULE_ROW');
  }
  return { core, rows };
}
const editing = () => Boolean(document.querySelector('.modal.show, [role="dialog"]')
  || document.activeElement?.matches('input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled]), [contenteditable="true"]'));
const saving = state => Object.values(state[portalApi.reducerPath]?.mutations || {}).some(item => item.status === 'pending');

// All schedule datasets share the existing connection/coordinator and this refresh policy.
// Components supply only their dataset name and state application callback.
export default function useScheduleRealtime({ view = 'main', blocked = false, enabled = true, apply, additionalResources = [] }) {
  const store = useStore();
  const { mode } = useRealtimeState();
  const url = useSelector(state => state.apiUrls?.urls?.fullSchedule);
  const user = useSelector(state => state.user?.userDetails);
  const pendingSave = useSelector(saving);
  useSyncExternalStore(formActivity.subscribe, formActivity.snapshot);
  const [editingNow, setEditingNow] = useState(editing);
  useEffect(() => {
    const check = () => setEditingNow(editing());
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    document.addEventListener('focusin', check); document.addEventListener('focusout', check);
    return () => { observer.disconnect(); document.removeEventListener('focusin', check); document.removeEventListener('focusout', check); };
  }, []);
  const current = useRef();
  const paused = blocked || editingNow || pendingSave || formActivity.blocked();
  current.current = { url, user, view, blocked: paused, enabled, apply };
  useRealtimeResource({
    enabled: Boolean(enabled && url && ['domain', 'session'].includes(mode)),
    ownerKey: `schedule:${user?.localAccountId || user?.username}:${url}:${view}`,
    resources: ['portal:general-project-info', 'portal:full-schedule', 'portal:soldto-shipto-info', 'portal:price-breakdown-info', ...additionalResources],
    blocked: paused,
    refresh: async ({ signal }) => {
      const valid = () => !signal.aborted && current.current.enabled && !current.current.blocked
        && current.current.url === url && current.current.user === user && current.current.view === view
        && store.getState().apiUrls?.urls?.fullSchedule === url && store.getState().user?.userDetails === user
        && !editing() && !saving(store.getState()) && !store.getState().jobSchedule?.loading && !formActivity.blocked();
      if (!valid()) return false;
      let superseded = false;
      const check = () => { if (!valid()) superseded = true; };
      const release = store.subscribe(check);
      const releaseDraft = formActivity.subscribe(check);
      document.addEventListener('focusin', check);
      const observer = new MutationObserver(check);
      observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      try {
      const snapshots = await Promise.all(scheduleUrls(url, view).map(async endpoint => {
        try { return readScheduleRows(await store.dispatch(fetchBackgroundData({
          ...(view === 'main' ? fullScheduleRequest(endpoint) : { url: endpoint, contract: {} }), signal,
        }))); }
        catch (error) {
          if (statuses[view] && error?.status === 404 && /no projects found/i.test(JSON.stringify(error.data || error.details || error))) {
            return { core: {}, rows: [] };
          }
          throw error;
        }
      }));
      if (superseded || !valid()) return false;
      const rows = [...new Map(snapshots.flatMap(snapshot => snapshot.rows)
        .map(row => [String(row.ID ?? row.id), row])).values()];
      current.current.apply(rows, snapshots);
      return true;
      } finally { release(); releaseDraft(); observer.disconnect(); document.removeEventListener('focusin', check); }
    },
  });
}
